import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaUniversity, 
  FaShieldAlt, 
  FaCogs, 
  FaHandHoldingHeart,
  FaRegClock,
  FaRegCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaDollarSign,
  FaStopCircle,
  FaArrowLeft,
  FaArrowRight,
  FaInfoCircle,
  FaEnvelope,
  FaFileDownload
} from 'react-icons/fa';
import { causes } from '../data/causes';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentSelector from '../components/PaymentSelector';
import '../styles/Donate.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { generateDefaultSampleReceipt } from '../utils/sampleReceiptGenerator';
import environment from '../config/environment';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const CONTACT_EMAIL = "Donate@CauseandImpact.org";

const generalDonationContent = {
  title: "Support Professional Fiscal Sponsorship",
  description: "Your donation to Cause and Impact helps us provide essential services and support to impactful projects. We ensure every dollar contributes to strengthening our ability to serve and grow meaningful initiatives.",
  impactDescription: "Your support enables us to:",
  impactPoints: [
    "Provide professional fiscal sponsorship services",
    "Support new and growing nonprofit projects",
    "Maintain operational excellence",
    "Expand our capacity to serve more initiatives"
  ]
};

const Donate = () => {
  const { causeId } = useParams();
  const [cause, setCause] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { apiUrl } = environment();  
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [coverFees, setCoverFees] = useState(true);
  const [donationType, setDonationType] = useState('one-time');
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarType, setCalendarType] = useState('start');
  const [recurringConfig, setRecurringConfig] = useState({
    frequency: 'monthly',
    startDate: new Date(),
    endDate: null,
    distributionType: 'fixed',
    amount: '',
    totalAmount: '',
    installments: '',
  });
  const [donateOnDate, setDonateOnDate] = useState(new Date());
  const [dateError, setDateError] = useState(null);
  const [datePickerStep, setDatePickerStep] = useState(1);
  const [donationStep, setDonationStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [currentTier, setCurrentTier] = useState(null);

  const swipeHandlers = useSwipeable({
    onSwipedRight: () => goToPreviousStep(),
    onSwipedLeft: () => goToNextStep(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });
  // Encode the causeId for URLs with special characters
  const encodedCauseId = encodeURIComponent(causeId);
 // Add this useEffect for debugging
 useEffect(() => {
  console.log('Current apiUrl:', apiUrl);
  console.log('Current causeId:', causeId);
}, [apiUrl, causeId]);

useEffect(() => {
  const fetchCause = async () => {
    try {
      setLoading(true);
      const url = `${apiUrl}/api/causes/${encodeURIComponent(causeId)}`;
      console.log('Fetching cause:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received data:', data);
      
      if (data.status === 'success' && data.data.cause) {
        setCause(data.data.cause);
      }
    } catch (error) {
      console.error('Error in fetchCause:', error);
      setError('Unable to load cause details');
    } finally {
      setLoading(false);
    }
  };
  if (causeId && apiUrl) {
    fetchCause();
  }
}, [causeId, apiUrl]);

  if (loading) {
    return (
      <div className="donate-wrapper">
        <div className="donate-container">
          <div className="loading-state">Loading cause details...</div>
        </div>
      </div>
    );
  }

  if (error || !cause) {
    return (
      <div className="donate-wrapper">
        <div className="donate-container">
          <div className="error-state">
            <h2>Cause not found</h2>
            <p>The cause you're looking for doesn't exist.</p>
            <Link to="/donate" className="back-link">Return to Causes</Link>
          </div>
        </div>
      </div>
    );
  }

  // Update the suggested amounts based on the selected cause's impact tiers
  const getSuggestedAmounts = (cause) => {
    // Common donation amounts without impact text
    const commonAmounts = [
      { amount: 25 },
      { amount: 50 },
      { amount: 100 },
      { amount: 250 }
    ];

    if (!cause || !cause.impactTiers || !Array.isArray(cause.impactTiers)) {
      return commonAmounts.map(item => ({
        ...item,
        displayAmount: item.amount.toLocaleString()
      }));
    }

    // Campaign-specific impact tiers
    const campaignTiers = cause.impactTiers.map(tier => ({
      amount: tier.level,
      impact: tier.impact,
      allocation: tier.allocation,
      isCampaignTier: true
    }));

    // Combine both arrays and sort by amount
    return [...commonAmounts, ...campaignTiers]
      .sort((a, b) => a.amount - b.amount)
      .map(item => ({
        ...item,
        displayAmount: item.amount.toLocaleString()
      }));
  };

  // Get suggested amounts based on the selected cause
  const suggestedAmounts = getSuggestedAmounts(cause);

  // Update the amount selection to show impact
  const updateSelectedAmount = (newAmount) => {
    setAmount(newAmount);
    setCustomAmount('');
    
    // Find the current impact tier
    const tier = cause.impactTiers.find(t => 
      newAmount >= t.level && 
      (newAmount < cause.impactTiers[cause.impactTiers.indexOf(t) + 1]?.level || 
       t === cause.impactTiers[cause.impactTiers.length - 1])
    );
    setCurrentTier(tier);
  };

  // Recurring payment options
  const frequencies = [
    { value: 'weekly', label: 'Weekly', icon: <FaCalendarWeek /> },
    { value: 'biweekly', label: 'Bi-weekly', icon: <FaCalendarDay /> },
    { value: 'monthly', label: 'Monthly', icon: <FaCalendarAlt /> },
    { value: 'quarterly', label: 'Quarterly', icon: <FaCalendarAlt /> },
    { value: 'annually', label: 'Annually', icon: <FaCalendarAlt /> }
  ];

  // Predefined distribution patterns
  const distributionPatterns = [
    { 
      label: 'Fixed Amount', 
      value: 'fixed',
      description: 'Same amount every payment'
    },
    { 
      label: 'Total Distribution', 
      value: 'total',
      description: 'Spread total amount across payments'
    }
  ];

  const calculateTotal = () => {
    const baseAmount = Number(amount || customAmount);
    return coverFees ? baseAmount * 1.05 : baseAmount;
  };

  const calculateAdminFee = () => {
    const baseAmount = Number(amount || customAmount);
    return baseAmount * 0.05;
  };

  // Calculate per-payment amount
  const calculatePerPayment = () => {
    if (recurringConfig.totalAmount && recurringConfig.installments) {
      return (parseFloat(recurringConfig.totalAmount) / parseInt(recurringConfig.installments)).toFixed(2);
    }
    return amount || customAmount || 0;
  };

  const validateDateRange = (startDate, endDate, frequency) => {
    const start = new Date(startDate);
    
    // Check if end date is provided
    if (!endDate) {
      return {
        isValid: false,
        message: "Please select an end date for your recurring donation"
      };
    }
    
    const end = new Date(endDate);
    
    // Calculate minimum period based on frequency
    const minPeriods = {
      weekly: 2,
      biweekly: 2,
      monthly: 2,
      quarterly: 2,
      annually: 2
    };

    const periodInMs = {
      weekly: 7 * 24 * 60 * 60 * 1000,
      biweekly: 14 * 24 * 60 * 60 * 1000,
      monthly: 30 * 24 * 60 * 60 * 1000,
      quarterly: 90 * 24 * 60 * 60 * 1000,
      annually: 365 * 24 * 60 * 60 * 1000
    };

    const diffTime = end.getTime() - start.getTime();
    const minimumGap = periodInMs[frequency] * minPeriods[frequency];

    if (diffTime < minimumGap) {
      return {
        isValid: false,
        message: `For ${frequency} donations, please select an end date at least ${minPeriods[frequency]} ${frequency.slice(0, -2)}s apart`
      };
    }

    return { isValid: true, message: null };
  };

  const calculateOccurrences = (startDate, endDate, frequency) => {
    if (!endDate) return 'Ongoing';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    
    const periodsInMs = {
      weekly: 7 * 24 * 60 * 60 * 1000,
      biweekly: 14 * 24 * 60 * 60 * 1000,
      monthly: 30 * 24 * 60 * 60 * 1000,
      quarterly: 90 * 24 * 60 * 60 * 1000,
      annually: 365 * 24 * 60 * 60 * 1000
    };

    const occurrences = Math.floor(diffTime / periodsInMs[frequency]);
    return occurrences > 0 ? occurrences : 0;
  };

  const handleDateSelection = (date, type) => {
    if (type === 'donate-on') {
      setDonateOnDate(date);
      return;
    }

    const newConfig = {
      ...recurringConfig,
      [type === 'start' ? 'startDate' : 'endDate']: date
    };

    // Validate the new date range
    const validation = validateDateRange(
      type === 'start' ? date : recurringConfig.startDate,
      type === 'end' ? date : recurringConfig.endDate,
      recurringConfig.frequency
    );

    if (!validation.isValid) {
      setDateError(validation.message);
      return;
    }

    setDateError(null);
    setRecurringConfig(newConfig);
    setShowCalendar(false);
  };

  // Add this helper function to calculate minimum end date
  const getMinimumEndDate = (startDate, frequency) => {
    const start = new Date(startDate);
    const minPeriods = {
      weekly: 2,
      biweekly: 2,
      monthly: 2,
      quarterly: 2,
      annually: 2
    };

    const addPeriod = {
      weekly: (date) => new Date(date.setDate(date.getDate() + (7 * minPeriods.weekly))),
      biweekly: (date) => new Date(date.setDate(date.getDate() + (14 * minPeriods.biweekly))),
      monthly: (date) => new Date(date.setMonth(date.getMonth() + minPeriods.monthly)),
      quarterly: (date) => new Date(date.setMonth(date.getMonth() + (3 * minPeriods.quarterly))),
      annually: (date) => new Date(date.setFullYear(date.getFullYear() + minPeriods.annually))
    };

    return addPeriod[frequency](new Date(start));
  };

  // Navigation handlers
  const goToNextStep = () => {
    setDirection(1);
    setDonationStep(current => {
      if (donationType === 'one-time') {
        return current === 1 ? 3 : current + 1;
      }
      return current + 1;
    });
  };

  const goToPreviousStep = () => {
    setDirection(-1);
    setDonationStep(current => {
      if (donationType === 'one-time') {
        return current === 3 ? 1 : current - 1;
      }
      return current - 1;
    });
  };

  // Animation variants
  const pageVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const scrollToAdminInfo = () => {
    const adminSection = document.querySelector('.admin-fee-section');
    if (adminSection) {
      adminSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add highlight animation
      adminSection.classList.add('highlight-section');
      setTimeout(() => {
        adminSection.classList.remove('highlight-section');
      }, 2000);
    }
  };

  // Add this helper function if it doesn't exist
  const formatAmount = (value) => {
    if (!value) return '';
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.toString().replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) return parts[0] + '.' + parts[1];
    return cleanValue;
  };

  // Add this helper function for consistent currency formatting
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const isGeneralDonation = cause?.id === 0;

  // Add this helper function
  const isAmountValid = () => {
    return (amount && amount > 0) || (customAmount && Number(customAmount) > 0);
  };

  const handleDownloadSampleReceipt = async (e) => {
    e.preventDefault(); // Prevent any default button behavior
    try {
      console.log('Generating sample receipt...'); // Debug log
      const doc = await generateDefaultSampleReceipt();
      if (!doc) {
        throw new Error('Failed to generate receipt document');
      }
      doc.save('Sample-Donation-Receipt.pdf');
    } catch (error) {
      console.error('Error generating sample receipt:', error);
    }
  };

  return (
    <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID }}>
      <Elements stripe={stripePromise}>
        <div className="donate-wrapper">
          <div className="donate-container">
            <div className="donate-header">
              {cause.image && !isGeneralDonation && (
                <img 
                  src={cause.image} 
                  alt={cause.name} 
                  className="cause-logo"
                />
              )}
              <h1>
                {isGeneralDonation ? generalDonationContent.title : `Support ${cause.name}`}
              </h1>
              <p className="impact-statement">
                {isGeneralDonation ? generalDonationContent.description : cause.shortDescription}
              </p>
              {isGeneralDonation && (
                <div className="impact-points">
                  <h3>{generalDonationContent.impactDescription}</h3>
                  <ul>
                    {generalDonationContent.impactPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Campaign Highlights */}
            {/* {campaigns.length > 0 && (
              <div className="campaigns-highlight">
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="campaign-progress-card">
                    <h3>{campaign.title}</h3>
                    <p>{campaign.description}</p>
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{width: `${(campaign.currentAmount/campaign.targetAmount) * 100}%`}}
                        />
                      </div>
                      <div className="progress-stats">
                        <span>${campaign.currentAmount.toLocaleString()} raised</span>
                        <span>of ${campaign.targetAmount.toLocaleString()}</span>
                      </div>
                    </div>
                    <p className="campaign-impact">{campaign.impact}</p>
                  </div>
                ))}
              </div>
            )} */}

            {/* Single Unified Donation Module */}
            <div className="donation-module" {...swipeHandlers}>
              <div className="step-progress">
                <div className={`step-dot ${donationStep >= 1 ? 'completed' : ''}`}>
                  <span className="step-label">Type</span>
                </div>
                {donationType === 'recurring' && (
                  <div className={`step-dot ${donationStep >= 2 ? 'completed' : ''}`}>
                    <span className="step-label">Schedule</span>
                  </div>
                )}
                <div className={`step-dot ${donationStep >= 3 ? 'completed' : ''}`}>
                  <span className="step-label">Amount</span>
                </div>
                <div className={`step-dot ${donationStep === 4 ? 'active' : ''}`}>
                  <span className="step-label">Review</span>
                </div>
              </div>

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={donationStep}
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                >
                  {/* Step 1: Donation Type Selection */}
                  {donationStep === 1 && (
                    <div className="donation-type-selection">
                      <h2>How would you like to make your impact?</h2>
                      <div className="donation-type-selector">
                        <button 
                          className={`type-button ${donationType === 'one-time' ? 'selected' : ''}`}
                          onClick={() => {
                            setDonationType('one-time');
                            setDonationStep(3); // Skip to amount selection for one-time
                          }}
                        >
                          <FaRegClock />
                          One-time Gift
                        </button>
                        <button 
                          className={`type-button ${donationType === 'recurring' ? 'selected' : ''}`}
                          onClick={() => {
                            setDonationType('recurring');
                            setDonationStep(2); // Go to recurring options
                          }}
                        >
                          <FaRegCalendarAlt />
                          Recurring Gift
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Schedule Configuration (Recurring only) */}
                  {donationStep === 2 && donationType === 'recurring' && (
                    <div className="schedule-configuration">
                      <h2>Choose Your Donation Schedule</h2>
                      <div className="frequency-selector">
                        {frequencies.map(freq => (
                          <button
                            key={freq.value}
                            className={`frequency-option ${recurringConfig.frequency === freq.value ? 'selected' : ''}`}
                            onClick={() => setRecurringConfig({
                              ...recurringConfig,
                              frequency: freq.value
                            })}
                          >
                            {freq.icon}
                            <span className="frequency-label">{freq.label}</span>
                          </button>
                        ))}
                      </div>

                      <div className="date-range-selector">
                        <DatePicker
                          selected={recurringConfig.startDate}
                          onChange={(date) => handleDateSelection(date, 'start')}
                          minDate={new Date()}
                          placeholderText="Select start date"
                          className="date-input"
                          calendarClassName="dark-calendar"
                        />
                        <DatePicker
                          selected={recurringConfig.endDate}
                          onChange={(date) => handleDateSelection(date, 'end')}
                          minDate={getMinimumEndDate(recurringConfig.startDate, recurringConfig.frequency)}
                          placeholderText="Select end date"
                          className="date-input"
                          calendarClassName="dark-calendar"
                        />
                      </div>

                      {dateError && (
                        <div className="date-error">
                          {dateError}
                        </div>
                      )}

                      {recurringConfig.startDate && recurringConfig.endDate && (
                        <div className="schedule-summary">
                          <div className="summary-item">
                            <span className="label">Total Payments:</span>
                            <span className="value">
                              {calculateOccurrences(
                                recurringConfig.startDate,
                                recurringConfig.endDate,
                                recurringConfig.frequency
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3: Amount Selection */}
                  {donationStep === 3 && (
                    <div className="amount-selection">
                      <h2>Choose Your Donation Amount</h2>
                      
                      {/* Impact Tiers Progress Visualization */}
                      <div className="impact-tiers-progress">
                        <h3>Campaign Impact Milestones</h3>
                        <div className="tiers-timeline">
                          {(cause.impactTiers || []).map((tier, index) => {
                            const progress = (cause.raisedAmount || 0) / tier.level * 100;
                            
                            return (
                              <div key={index} className="tier-milestone">
                                <div className="tier-progress-line">
                                  <div 
                                    className="tier-progress-fill"
                                    style={{
                                      width: `${Math.min(progress, 100)}%`
                                    }}
                                  />
                                  <div 
                                    className="progress-marker"
                                    style={{
                                      left: `${Math.min(progress, 100)}%`
                                    }}
                                  />
                                  <span className="current-amount-marker">
                                    ${(cause.raisedAmount || 0).toLocaleString()}
                                  </span>
                                  <span className="tier-goal-amount">
                                    ${tier.level.toLocaleString()}
                                  </span>
                                </div>
                                <div className="tier-content">
                                  <p className="tier-impact">{tier.impact}</p>
                                  <p className="tier-allocation">{tier.allocation}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="current-progress">
                          <span className="total-raised">
                            Total Raised: ${(cause.raisedAmount || 0).toLocaleString()}
                          </span>
                          <span className="next-milestone">
                            Next Milestone: $
                            {(cause.impactTiers || [])
                              .find((tier) => tier.level > (cause.raisedAmount || 0))
                              ?.level.toLocaleString() || 'Goal Achieved!'}
                          </span>
                        </div>
                      </div>

                      {/* Original Amount Selection Grid */}
                      <div className="amount-grid">
                        {suggestedAmounts.map(({ amount: suggAmount, displayAmount, impact, isCampaignTier }) => (
                          <button
                            key={suggAmount}
                            className={`amount-button ${amount === suggAmount ? 'selected' : ''} ${isCampaignTier ? 'campaign-tier' : ''}`}
                            onClick={() => {
                              setAmount(suggAmount);
                              setCustomAmount('');
                            }}
                          >
                            <span className="amount">${displayAmount}</span>
                            {isCampaignTier && impact && (
                              <span className="impact">{impact}</span>
                            )}
                          </button>
                        ))}
                        
                        <div className="custom-amount">
                          <div className="dollar-sign">$</div>
                          <input
                            type="text"
                            value={formatAmount(customAmount)}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d.]/g, '');
                              if (value === '' || (Number(value) >= 0 && !isNaN(value))) {
                                setCustomAmount(value);
                                setAmount('');
                              }
                            }}
                            placeholder="Enter amount"
                            className="custom-amount-input"
                            inputMode="decimal"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Review */}
                  {donationStep === 4 && (
                    <div className="donation-review">
                      <h2>Review Your Donation</h2>
                      
                      {/* Navigation - Moved to top */}
                      <div className="step-navigation">
                        <button 
                          className="nav-btn back"
                          onClick={goToPreviousStep}
                        >
                          <FaArrowLeft />
                          Back
                        </button>
                      </div>

                      <div className="review-summary">
                        <div className="review-item">
                          <span className="label">Type:</span>
                          <span className="value">
                            {donationType === 'recurring' ? 'Recurring Impact' : 'One-Time Gift'}
                          </span>
                        </div>
                        <div className="review-item">
                          <span className="label">Base Amount:</span>
                          <span className="value">{formatCurrency(amount || customAmount)}</span>
                        </div>
                        {donationType === 'recurring' && (
                          <>
                            <div className="review-item">
                              <span className="label">Frequency:</span>
                              <span className="value">{recurringConfig.frequency}</span>
                            </div>
                            <div className="review-item">
                              <span className="label">Schedule:</span>
                              <span className="value">
                                {recurringConfig.startDate.toLocaleDateString()} to{' '}
                                {recurringConfig.endDate.toLocaleDateString()}
                              </span>
                            </div>
                            <div className="review-item">
                              <span className="label">Total Payments:</span>
                              <span className="value">
                                {calculateOccurrences(
                                  recurringConfig.startDate,
                                  recurringConfig.endDate,
                                  recurringConfig.frequency
                                )}
                              </span>
                            </div>
                          </>
                        )}
                        
                        {/* Admin Fee - always show, but style differently based on coverFees */}
                        <div className={`review-item admin-fee ${!coverFees ? 'fee-deducted' : ''}`}>
                          <span className="label">
                            Administrative Fee (5%)
                            <button 
                              className="info-icon"
                              onClick={scrollToAdminInfo}
                              aria-label="Learn more about administrative fee"
                            >
                              <FaInfoCircle />
                            </button>
                          </span>
                          <span className="value">
                            {coverFees ? (
                              // If covering fees, show as additional
                              <>+{formatCurrency(calculateAdminFee())}</>
                            ) : (
                              // If not covering fees, show as deducted from base amount
                              <span className="deducted-fee">
                                {formatCurrency(calculateAdminFee())} deducted from your donation
                              </span>
                            )}
                          </span>
                        </div>

                        {/* Total Amount */}
                        <div className="review-item total">
                          <span className="label">Total Amount:</span>
                          <span className="value">{formatCurrency(calculateTotal())}</span>
                        </div>
                      </div>
                      
                      {/* Payment section */}
                      <div className="payment-section disabled">
                        <div className="payment-disabled-overlay">
                          <p>Donation processing coming soon!</p>
                          <button 
                            className="sample-receipt-button"
                            onClick={handleDownloadSampleReceipt}
                            type="button" // Explicitly set button type
                          >
                            <FaFileDownload /> Download Sample Tax Receipt
                          </button>
                        </div>
                        <PaymentSelector 
                          disabled={true}
                          onSubmit={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              {donationStep !== 1 && (
                <div className="navigation-buttons">
                  <button 
                    className="back-button" 
                    onClick={goToPreviousStep}
                  >
                    <FaArrowLeft /> Back
                  </button>
                  {donationStep !== 4 && (
                    <button 
                      className="continue-button" 
                      onClick={goToNextStep}
                      disabled={donationStep === 3 && !isAmountValid()}
                    >
                      Continue <FaArrowRight />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Detailed Fee Explanation Section */}
            <div className="fee-explanation-wrapper">
              <h3>Understanding Our 5% Administrative Fee</h3>
              <p>
                Your 5% contribution supports comprehensive fiscal sponsorship services that enable projects to focus entirely on their mission while ensuring full compliance and professional management:
              </p>
              <div className="fee-benefits">
                <div className="benefit-item">
                  <FaShieldAlt className="benefit-icon" />
                  <div className="benefit-text">
                    <strong>Legal & Compliance Framework</strong>
                    <p>Full 501(c)(3) tax-exempt status coverage, annual independent audits, IRS compliance, and legal protection under our organizational umbrella</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <FaUniversity className="benefit-icon" />
                  <div className="benefit-text">
                    <strong>Financial Management</strong>
                    <p>Professional accounting services, payroll processing, tax reporting, grant management, and transparent financial oversight ensuring donor trust</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <FaCogs className="benefit-icon" />
                  <div className="benefit-text">
                    <strong>Administrative Support</strong>
                    <p>Complete back-office services including HR management, insurance coverage, risk management, and operational infrastructure</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <FaHandHoldingHeart className="benefit-icon" />
                  <div className="benefit-text">
                    <strong>Donor & Grant Services</strong>
                    <p>Tax receipting, donor management, grant compliance monitoring, and reporting to ensure maximum impact of every contribution</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legal Footer with Full IRS Disclosure */}
            <div className="legal-footer">
              <p>
                {cause.name} is a fiscally sponsored project of Cause and Impact, Inc. (EIN: 32-0977089), 
                a Texas nonprofit corporation recognized by the IRS as a tax-exempt public charity under 
                Section 501(c)(3) of the Internal Revenue Code. All donations are tax-deductible to the 
                extent allowed by law. No goods or services were provided in exchange for your contribution.
              </p>
            </div>

            {showDisclaimer && (
              <div className="donation-disclaimer">
                <div className="disclaimer-content">
                  <button className="close-disclaimer" onClick={() => setShowDisclaimer(false)}>×</button>
                  <h3>🚧 Preview Mode</h3>
                  <p>
                    We're currently integrating with our banking partners to provide you with a secure and seamless donation experience. 
                    While the payment processing is temporarily paused, we invite you to explore our donation platform and see what's coming!
                  </p>
                  <p>
                    Want to make a donation now? We'd love to help!
                  </p>
                  <a 
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="contact-button"
                  >
                    <FaEnvelope /> Contact Our Donation Team
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </Elements>
    </PayPalScriptProvider>
  );
};

export default Donate;
